import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileSatsueiService } from '../services/mobileSatsueiService';
import { mobileUserInfoService } from '../services/mobileUserInfoService';
import { useApiError } from '../../shared/hooks/useApiError';
import { useAuth } from '../../shared/auth';
import { MessageDialog } from '../../shared/ui';
import Footer from '../components/Footer';

// CSS-in-JS スタイル定義
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },
  loading: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  contents: {
    display: 'block',
    padding: '25px 0px 50px 0px',
    maxWidth: '500px',
    margin: '0 auto'
  },
  satsueiArea: {
    padding: '10px 0'
  },
  objectIdInput: {
    width: '95%',
    display: 'block',
    margin: '50px auto 20px auto',
    padding: '4px',
    fontWeight: 'bold',
    textAlign: 'center',
    fontSize: '18pt',
    border: '1px solid #AAA',
    WebkitAppearance: 'none',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  satsueiButtonArea: {
    padding: '10px 0',
    borderBottom: '1px dashed #2b2222'
  },
  satsueiButton: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px auto',
    padding: '12px 8px',
    background: '#2B2222',
    borderRadius: '30px',
    textAlign: 'left',
    lineHeight: '30px',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    position: 'relative',
    transition: 'all 0.3s ease',
    minHeight: '54px',
    boxSizing: 'border-box',
    width: '45%'
  },
  barcodeButton: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px auto',
    padding: '12px 8px',
    background: '#2B2222',
    borderRadius: '30px',
    textAlign: 'left',
    lineHeight: '30px',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    position: 'relative',
    transition: 'all 0.3s ease',
    minHeight: '54px',
    boxSizing: 'border-box',
    width: '80%'
  },
  satsueiButtonHover: {
    background: '#444',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
  },
  satsueiButtonArea1: {
    margin: '0 auto',
    textAlign: 'center',
    display: 'flex',
    gap: '10px'
  },
  satsueiButton1_1: {
    width: '48%',
    margin: '0',
    minWidth: '140px'
  },
  satsueiButton1_2: {
    width: '48%',
    margin: '0',
    minWidth: '140px'
  },
  satsueiButton2: {
    width: '95%',
    minWidth: '280px'
  },
  buttonIcon: {
    background: '#FFF',
    margin: '0px 12px 0px 20px',
    padding: '3px',
    display: 'block',
    width: '30px',
    height: '30px',
    borderRadius: '15px',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    flexShrink: 0
  },
  selector: {
    marginTop: '10px'
  },
  caption: {
    width: '95%',
    margin: '4px auto',
    background: '#EEE',
    padding: '4px 12px',
    color: '#999',
    fontWeight: 'bold',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center'
  },
  captionIcon: {
    marginRight: '10px',
    width: '16px',
    height: '16px',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center'
  },
  select: {
    width: '95%',
    display: 'block',
    margin: '4px auto 14px auto',
    fontSize: '12pt',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    border: '1px solid #CCC',
    padding: '8px 12px',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  selectFocus: {
    outline: 'none',
    borderColor: '#F93535',
    boxShadow: '0 0 0 2px rgba(249, 53, 53, 0.2)'
  }
};

const AppSatsuei = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setLoading(false));

  // 関数の参照を安定化
  const handleApiErrorRef = useRef(handleApiError);

  useEffect(() => {
    handleApiErrorRef.current = handleApiError;
  }, [handleApiError]);
  
  // UI状態
  const [loading, setLoading] = useState(false); // ローディング状態
  const [objectId, setObjectId] = useState(''); // オブジェクトID
  const [objectIdAutoFlg, setObjectIdAutoFlg] = useState(''); // 自動採番フラグ
  const [backUrl, setBackUrl] = useState(''); // 戻り先URL
  const [isSaveLocal, setIsSaveLocal] = useState(''); // ローカル保存フラグ
  const [userKyotenId, setUserKyotenId] = useState(''); // ユーザー拠点ID
  const [thetaCameraUsage, setThetaCameraUsage] = useState(0); // THETAカメラ利用可否（0: 不可, 1: 可）
  
  // フォームデータ
  const [satsueiMemoCategory, setSatsueiMemoCategory] = useState('0'); // 撮影メモカテゴリー
  const [hozonFolder, setHozonFolder] = useState(0); // 保存フォルダー
  const [satsueiGuide, setSatsueiGuide] = useState(''); // 撮影ガイド
  
  // マスターデータ
  const [masterData, setMasterData] = useState({
    satsueiMemoCategories: [], // 撮影メモカテゴリー一覧
    imageFolders: [], // 画像フォルダー一覧
    satsueiGuides: [] // 撮影ガイド一覧
  });
  

  // コンポーネント初期化
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        // 初期表示用データを取得
        const initialData = await mobileSatsueiService.getInitialData({});
        
        // ビジネスロジックエラーのチェック
        if (initialData.returnCode !== '200') {
          MessageDialog.show('エラー', '初期化に失敗しました。', null, 'error');
          setLoading(false);
          return;
        }
        
        // 取得したデータでstateを更新
        if (initialData.backUrl!=='') {
          setBackUrl(initialData.backUrl);
        }
        if (initialData.isSaveLocal!=='') {
          setIsSaveLocal(initialData.isSaveLocal);
        }
        if(initialData.userKyotenId!=='') {
          setUserKyotenId(initialData.userKyotenId);
        }
        if(initialData.objectIdAutoFlg!=='') {
          setObjectIdAutoFlg(initialData.objectIdAutoFlg);
        }
        
        // マスターデータと現在ユーザーのTHETA権限を並行して取得
        const [
          satsueiMemoCategories,
          imageFolders,
          pixUserResponse
        ] = await Promise.all([
          mobileSatsueiService.getSatsueiMemoCategory(),
          mobileSatsueiService.getImageFolderList(),
          mobileUserInfoService.getPixUser(user).catch(() => null)
        ]);

        if (pixUserResponse?.returnCode === '200' && pixUserResponse.pixUserList?.length > 0) {
          const usage = pixUserResponse.pixUserList[0].thetaCameraUsage;
          setThetaCameraUsage(Number(usage) === 1 ? 1 : 0);
        }
        
        setMasterData(prev => ({
          ...prev,
          satsueiMemoCategories: Array.isArray(satsueiMemoCategories.satsueiMemoCategoryList) ? satsueiMemoCategories.satsueiMemoCategoryList : [],
          imageFolders: Array.isArray(imageFolders.imageFolderList) ? imageFolders.imageFolderList : [],
        }));
        
        // 自動採番モードの場合は最大IDを取得
        if (initialData.objectIdAutoFlg === 1) {
          const maxIdData = await mobileSatsueiService.getMaxObjectId();
          
          // ビジネスロジックエラーのチェック
          if (maxIdData.returnCode !== '200') {
            MessageDialog.show('エラー', '最大IDの取得に失敗しました。', null, 'error');
            setLoading(false);
            return;
          }
          
          setObjectId(maxIdData.objectId);
        }
        
      } catch (error) {
        handleApiError(new Error('初期化に失敗しました。'));
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 撮影ガイドリスト読み込み
  const loadSatsueiGuideList = useCallback(async (categoryId) => {
    try {
      // まず撮影ガイドをクリア
      setMasterData(prev => ({
        ...prev,
        satsueiGuides: []
      }));
      
      const guides = await mobileSatsueiService.getSatsueiGuideList(categoryId);
      
      // ビジネスロジックエラーのチェック
      if (guides.returnCode !== '200') {
        MessageDialog.show('エラー', '撮影ガイドの取得に失敗しました。', null, 'error');
        return;
      }
      
      setMasterData(prev => ({
        ...prev,
        satsueiGuides: Array.isArray(guides.satsueiGuideList) ? guides.satsueiGuideList : []
      }));
    } catch (error) {
      handleApiError(new Error('撮影ガイドの取得に失敗しました。'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 撮影ガイドカテゴリー変更処理
  useEffect(() => {
    if (satsueiMemoCategory != null && satsueiMemoCategory !== '') {
      loadSatsueiGuideList(satsueiMemoCategory);
    } else {
      // カテゴリーが選択されていない場合は撮影ガイドをリセット
      setSatsueiGuide('');
    }
  }, [satsueiMemoCategory, loadSatsueiGuideList]);

  // 新規撮影（手動採番）
  const handleStartCamera = useCallback(async () => {
    if (!objectId.trim()) {
      MessageDialog.show('エラー', 'IDを入力してください。');
      return;
    }

    // IDの形式チェック
    const regex = /^([a-zA-Z0-9]|_|-)+$/;
    if (!regex.test(objectId)) {
      MessageDialog.show('エラー', 'IDは半角英数、アンダーバー、ハイフンで入力してください。');
      return;
    }

    setLoading(true);
    try {
      // オブジェクト数を確認
      const objectCountData = await mobileSatsueiService.getObjectCount(objectId);

      if(objectCountData.returnCode !== '200'){
        MessageDialog.show('エラー', 'オブジェクト数の取得に失敗しました。', null, 'error');
        return;
      }
      
      if (objectCountData.objectCount >= 1) {
        // 撮影済みIDなので詳細ページに移動
        MessageDialog.show('メッセージ', '撮影済みIDなので詳細ページに移動します。', () => {
          navigate(`/appObjectDetail?objectId=${objectId}&objectIdAutoFlg=${objectIdAutoFlg}&from=satsuei`);
        });
        return;
      }

      // カメラ起動用データを準備
      const cameraData = await mobileSatsueiService.prepareCameraData({
        objectId: objectId,
        satsueiMemoCategoryId: satsueiMemoCategory,
        userKyotenId: userKyotenId,
        imageFolderId: hozonFolder,
        satsueiGuideId: satsueiGuide,
        backUrl: backUrl + '?objectId=' + objectId + '&objectIdAutoFlg=' + objectIdAutoFlg + '&from=search',
        isSaveLocal: isSaveLocal,
        thetaCameraUsage: Number(thetaCameraUsage)
      });

      // ネイティブアプリのカメラを起動
      mobileSatsueiService.startUpCamera(cameraData);

    } catch (error) {
      handleApiError(new Error('カメラ起動に失敗しました。'));
    } finally {
      setLoading(false);
    }
  }, [objectId, satsueiMemoCategory, hozonFolder, satsueiGuide, backUrl, isSaveLocal, objectIdAutoFlg, navigate, userKyotenId, thetaCameraUsage, handleApiError]);

  // 新規撮影（自動採番）
  const handleStartCameraAuto = useCallback(async () => {
    setLoading(true);
    try {
      // 最大IDを取得
      const maxIdData = await mobileSatsueiService.getMaxObjectId();

      if(maxIdData.returnCode !== '200'){
        MessageDialog.show('エラー', '最大IDの取得に失敗しました。', null, 'error');
        return;
      }

      const newObjectId = maxIdData.objectId;
      setObjectId(newObjectId);

      MessageDialog.show('メッセージ', `${newObjectId}で登録を行います。`, async () => {
        try {
          // オブジェクトを登録
          await mobileSatsueiService.registObject(
            newObjectId,
            satsueiMemoCategory,
            userKyotenId,
            satsueiGuide
          );

          // カメラ起動用データを準備
          const cameraData = await mobileSatsueiService.prepareCameraData({
            objectId: newObjectId,
            satsueiMemoCategoryId: satsueiMemoCategory,
            userKyotenId: userKyotenId,
            imageFolderId: hozonFolder,
            satsueiGuideId: satsueiGuide,
            backUrl: backUrl + '?objectId=' + newObjectId + '&objectIdAutoFlg=' + objectIdAutoFlg + '&from=search',
            isSaveLocal: isSaveLocal,
            thetaCameraUsage: Number(thetaCameraUsage)
          });

          mobileSatsueiService.startUpCamera(cameraData);
        } catch (error) {
          handleApiError(new Error('登録に失敗しました。'));
        }
      });
      
    } catch (error) {
      handleApiError(new Error('カメラ起動に失敗しました。'));
    } finally {
      setLoading(false);
    }
  }, [satsueiMemoCategory, hozonFolder, satsueiGuide, backUrl, isSaveLocal, objectIdAutoFlg, userKyotenId, handleApiError]);

  // 編集（手動採番）
  const handleStartObjectDetail = useCallback(async () => {
    if (!objectId.trim()) {
      MessageDialog.show('エラー', 'IDを入力してください。');
      return;
    }

    // IDの形式チェック
    const regex = /^([a-zA-Z0-9]|_|-)+$/;
    if (!regex.test(objectId)) {
      MessageDialog.show('エラー', 'IDは半角英数、アンダーバー、ハイフンで入力してください。');
      return;
    }

    setLoading(true);
    try {
      // オブジェクト数を確認
      const objectCountData = await mobileSatsueiService.getObjectCount(objectId);

      if(objectCountData.returnCode !== '200'){
        MessageDialog.show('エラー', 'オブジェクト数の取得に失敗しました。', null, 'error');
        return;
      }
      
      if (objectCountData.objectCount === 0) {
        MessageDialog.show('エラー', '登録されていないIDです。');
        return;
      }

      // 撮影カテゴリーを設定（0も有効な値）
      if (objectCountData.satsueiMemoCategoryId != null) {
        setSatsueiMemoCategory(objectCountData.satsueiMemoCategoryId);
      }

        // オブジェクト詳細を表示
        navigate(`/appObjectDetail?objectId=${objectId}&objectIdAutoFlg=${objectIdAutoFlg}&from=satsuei`);

    } catch (error) {
      handleApiError(new Error('撮影データの表示に失敗しました。'));
    } finally {
      setLoading(false);
    }
  }, [objectId, objectIdAutoFlg, navigate, handleApiError]);

  // 編集（自動採番）
  const handleStartObjectDetailAuto = useCallback(async () => {
    setLoading(true);
    try {
      if (!objectId.trim()) {
        // 最大IDを取得して設定
        const maxIdData = await mobileSatsueiService.getMaxObjectId();

        if(maxIdData.returnCode !== '200'){
          MessageDialog.show('エラー', '最大IDの取得に失敗しました。', null, 'error');
          return;
        }

        setObjectId(maxIdData.objectId);

        MessageDialog.show('メッセージ', `${maxIdData.objectId}で登録を行います。`, async () => {
          try {
            // オブジェクトを登録
            await mobileSatsueiService.registObject(
              maxIdData.objectId,
              satsueiMemoCategory,
              userKyotenId,
              satsueiGuide
            );
            
            navigate(`/appObjectDetail?objectId=${maxIdData.objectId}&objectIdAutoFlg=${objectIdAutoFlg}&from=satsuei`);
          } catch (error) {
            handleApiError(new Error('登録に失敗しました。'));
          }
        });
      } else {
        // オブジェクト数を確認
        const objectCountData = await mobileSatsueiService.getObjectCount(objectId);

        if(objectCountData.returnCode !== '200'){
          MessageDialog.show('エラー', 'オブジェクト数の取得に失敗しました。', null, 'error');
          return;
        }
        
        if (objectCountData.objectCount === 0) {
          MessageDialog.show('エラー', '登録されていないIDです。');
          return;
        }

        // 撮影カテゴリーを設定（0も有効な値）
        if (objectCountData.satsueiMemoCategoryId != null) {
          setSatsueiMemoCategory(objectCountData.satsueiMemoCategoryId);
        }

        // オブジェクト詳細を表示
        navigate(`/appObjectDetail?objectId=${objectId}&objectIdAutoFlg=${objectIdAutoFlg}&from=satsuei`);
      }
      
    } catch (error) {
      handleApiError(new Error('撮影データの表示に失敗しました。'));
    } finally {
      setLoading(false);
    }
  }, [objectId, satsueiMemoCategory, satsueiGuide, objectIdAutoFlg, navigate, userKyotenId, handleApiError]);

  // THETA撮影開始
  const handleStartTheta = useCallback(async () => {
    if (!objectId.trim()) {
      MessageDialog.show('エラー', 'IDを入力してください。', null, 'error');
      return;
    }

    const regex = /^([a-zA-Z0-9]|_|-)+$/;
    if (!regex.test(objectId)) {
      MessageDialog.show('エラー', 'IDは半角英数、アンダーバー、ハイフンで入力してください。', null, 'error');
      return;
    }

    setLoading(true);
    try {
      const cameraData = await mobileSatsueiService.prepareCameraData({
        objectId: objectId,
        satsueiMemoCategoryId: satsueiMemoCategory,
        userKyotenId: userKyotenId,
        imageFolderId: hozonFolder,
        satsueiGuideId: satsueiGuide,
        backUrl: backUrl + '?objectId=' + objectId + '&objectIdAutoFlg=' + objectIdAutoFlg + '&from=search',
        isSaveLocal: isSaveLocal,
        cameraType: 'theta',
        thetaCameraUsage: Number(thetaCameraUsage)
      });

      mobileSatsueiService.startUpCamera(cameraData);
    } catch (error) {
      handleApiError(new Error('THETA撮影の起動に失敗しました。'));
    } finally {
      setLoading(false);
    }
  }, [objectId, satsueiMemoCategory, hozonFolder, satsueiGuide, backUrl, isSaveLocal, objectIdAutoFlg, userKyotenId, handleApiError]);

  // バーコード読み取り開始(手動採番のみ)
  const handleStartCodeReader = useCallback(async () => {
    setLoading(true);

    try {
      // バーコード読み取り用データを準備（IDはバーコードから取得するため空でOK）
      const cameraData = await mobileSatsueiService.prepareCameraData({
        objectId: objectId || '',
        satsueiMemoCategoryId: satsueiMemoCategory,
        userKyotenId: userKyotenId,
        imageFolderId: hozonFolder,
        satsueiGuideId: satsueiGuide,
        backUrl: backUrl + '?objectIdAutoFlg=' + objectIdAutoFlg + '&from=search',
        isSaveLocal: isSaveLocal
      });

      // ネイティブアプリのバーコードリーダーを直接起動
      mobileSatsueiService.startUpCodeReader(cameraData);

    } catch (error) {
      handleApiError(new Error('コードリーダーの起動に失敗しました。'));
    } finally {
      setLoading(false);
    }
  }, [objectId, satsueiMemoCategory, hozonFolder, satsueiGuide, backUrl, isSaveLocal, objectIdAutoFlg, userKyotenId, handleApiError]);

  // ネイティブのバーコード読み取り完了後に呼ばれるグローバル関数を登録
  useEffect(() => {
    window.sendCodeInfo = async (data) => {
      const scannedObjectId = data.objectId;
      if (!scannedObjectId) return;

      setLoading(true);
      try {
        // 読み取ったIDで既存チェック
        const objectCountData = await mobileSatsueiService.getObjectCount(scannedObjectId);
        if (objectCountData.returnCode !== '200') {
          MessageDialog.show('エラー', 'オブジェクト数の取得に失敗しました。');
          return;
        }

        if (objectCountData.objectCount >= 1) {
          // 既存IDなので詳細ページに移動
          MessageDialog.show('メッセージ', '撮影済みIDなので詳細ページに移動します。', () => {
            navigate(`/appObjectDetail?objectId=${scannedObjectId}&objectIdAutoFlg=${objectIdAutoFlg}&from=satsuei`);
          });
        } else {
          // 新規ID → そのままカメラ起動（バーコード読み取り時のパラメータをそのまま使用）
          const cameraData = {
            ...data,
            backUrl: backUrl + '?objectId=' + scannedObjectId + '&objectIdAutoFlg=' + objectIdAutoFlg + '&from=search',
          };
          mobileSatsueiService.startUpCamera(cameraData);
        }
      } catch (error) {
        handleApiError(new Error('コード読み取り後の処理に失敗しました。'));
      } finally {
        setLoading(false);
      }
    };

    return () => {
      delete window.sendCodeInfo;
    };
  }, [backUrl, objectIdAutoFlg, navigate, handleApiError]);

  return (
    <div style={styles.container}>
      {loading && (
        <div style={styles.loading}>
          <div>読み込み中...</div>
        </div>
      )}
      
      {/* メインコンテンツ */}
      {!loading && (
        <div style={styles.contents}>
        <div style={styles.satsueiArea}>
          {/* ID入力フィールド */}
          <input
            type="text"
            value={objectId}
            onChange={(e) => setObjectId(e.target.value)}
            placeholder="ID"
            style={styles.objectIdInput}
            // disabled={objectIdAutoFlg === 1}
          />
          
          {/* ボタンエリア */}
          <div style={styles.satsueiButtonArea}>
            <div style={styles.satsueiButtonArea1}>
              <button
                style={styles.satsueiButton}
                onClick={objectIdAutoFlg === 0 ? handleStartCamera : handleStartCameraAuto}
                onMouseEnter={(e) => {
                  e.target.style.background = styles.satsueiButtonHover.background;
                  e.target.style.transform = styles.satsueiButtonHover.transform;
                  e.target.style.boxShadow = styles.satsueiButtonHover.boxShadow;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = styles.satsueiButton.background;
                  e.target.style.transform = '';
                  e.target.style.boxShadow = '';
                }}
              >
                <div style={{
                  ...styles.buttonIcon,
                  backgroundImage: 'url(/core/img/icon/input.png)',
                  backgroundSize: '80%'
                }}></div>
                新規撮影
              </button>
              <button
                style={styles.satsueiButton}
                onClick={objectIdAutoFlg === 0 ? handleStartObjectDetail : handleStartObjectDetailAuto}
                onMouseEnter={(e) => {
                  e.target.style.background = styles.satsueiButtonHover.background;
                  e.target.style.transform = styles.satsueiButtonHover.transform;
                  e.target.style.boxShadow = styles.satsueiButtonHover.boxShadow;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = styles.satsueiButton.background;
                  e.target.style.transform = '';
                  e.target.style.boxShadow = '';
                }}
              >
                <div style={{
                  ...styles.buttonIcon,
                  backgroundImage: 'url(/core/img/icon/input.png)',
                  backgroundSize: '80%'
                }}></div>
                編集
              </button>
            </div>
          </div>
          
          {/* THETAでの新規撮影ボタン（ユーザーがTHETA利用可の場合のみ表示） */}
          {Number(thetaCameraUsage) === 1 && (
            <div style={styles.satsueiButtonArea}>
              <button
                style={styles.barcodeButton}
                onClick={handleStartTheta}
                onMouseEnter={(e) => {
                  e.target.style.background = styles.satsueiButtonHover.background;
                  e.target.style.transform = styles.satsueiButtonHover.transform;
                  e.target.style.boxShadow = styles.satsueiButtonHover.boxShadow;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = styles.satsueiButton.background;
                  e.target.style.transform = '';
                  e.target.style.boxShadow = '';
                }}
              >
                <div style={{
                  ...styles.buttonIcon,
                  backgroundImage: 'url(/core/img/icon/theta.png)',
                  backgroundSize: '80%'
                }}></div>
                THETAでの新規撮影
              </button>
            </div>
          )}

          {/* バーコード読み取りボタン */}
          {objectIdAutoFlg === 0 && (
            <div style={styles.satsueiButtonArea}>
              <button
                style={styles.barcodeButton}
                onClick={handleStartCodeReader}
                onMouseEnter={(e) => {
                  e.target.style.background = styles.satsueiButtonHover.background;
                  e.target.style.transform = styles.satsueiButtonHover.transform;
                  e.target.style.boxShadow = styles.satsueiButtonHover.boxShadow;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = styles.satsueiButton.background;
                  e.target.style.transform = '';
                  e.target.style.boxShadow = '';
                }}
              >
                <div style={{
                  ...styles.buttonIcon,
                  backgroundImage: 'url(/core/img/icon/barcode.png)',
                  backgroundSize: '80%'
                }}></div>
                コードを読み取って撮影
              </button>
            </div>
          )}
          
          {/* 設定項目 */}
          <div style={styles.selector}>
            {/* 撮影カテゴリー */}
            {masterData.satsueiMemoCategories.length > 1 && (
              <div>
                <div style={styles.caption}>
                  <div style={{
                    ...styles.captionIcon,
                    backgroundImage: 'url(/core/img/icon/maintenance.png)'
                  }}></div>
                  撮影カテゴリー
              </div>
                <select
                  value={satsueiMemoCategory}
                  onChange={(e) => setSatsueiMemoCategory(e.target.value)}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.outline = styles.selectFocus.outline;
                    e.target.style.borderColor = styles.selectFocus.borderColor;
                    e.target.style.boxShadow = styles.selectFocus.boxShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.outline = '';
                    e.target.style.borderColor = '';
                    e.target.style.boxShadow = '';
                  }}
                >
                  <option value="0">未指定</option>
                  {masterData.satsueiMemoCategories
                    .filter(category => category.satsueiMemoCategoryId !== 0 && category.satsueiMemoCategoryId !== '0')
                    .map(category => (
                      <option key={category.satsueiMemoCategoryId} value={category.satsueiMemoCategoryId}>
                        {category.satsueiMemoCategoryName}
                      </option>
                    ))}
                </select>
            </div>
          )}
            
            {/* 保存フォルダー */}
            {masterData.imageFolders.length > 0 && (
              <div>
                <div style={styles.caption}>
                  <div style={{
                    ...styles.captionIcon,
                    backgroundImage: 'url(/core/img/icon/folder.png)'
                  }}></div>
                  保存フォルダー
        </div>
                <select
                  value={hozonFolder}
                  onChange={(e) => setHozonFolder(parseInt(e.target.value, 10))}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.outline = styles.selectFocus.outline;
                    e.target.style.borderColor = styles.selectFocus.borderColor;
                    e.target.style.boxShadow = styles.selectFocus.boxShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.outline = '';
                    e.target.style.borderColor = '';
                    e.target.style.boxShadow = '';
                  }}
                >
                  <option value="0">未指定</option>
                  {masterData.imageFolders.map(folder => (
                    <option key={folder.imageFolderId} value={folder.imageFolderId}>
                      {folder.imageFolderName}
                    </option>
                  ))}
                </select>
          </div>
            )}
            
            {/* 撮影ガイド */}
            {satsueiMemoCategory != null && satsueiMemoCategory !== ''
                 && masterData.satsueiGuides.length > 0 && (
              <div>
                <div style={styles.caption}>
                  <div style={{
                    ...styles.captionIcon,
                    backgroundImage: 'url(/core/img/icon/photo-camera_2.png)'
                  }}></div>
                  撮影ガイド
          </div>
                <select
                  value={satsueiGuide}
                  onChange={(e) => setSatsueiGuide(e.target.value)}
                  style={styles.select}
                  onFocus={(e) => {
                    e.target.style.outline = styles.selectFocus.outline;
                    e.target.style.borderColor = styles.selectFocus.borderColor;
                    e.target.style.boxShadow = styles.selectFocus.boxShadow;
                  }}
                  onBlur={(e) => {
                    e.target.style.outline = '';
                    e.target.style.borderColor = '';
                    e.target.style.boxShadow = '';
                  }}
                >
                  <option value="">使用しない</option>
                  {masterData.satsueiGuides.map((guide, index) => (
                    <option key={`${guide.satsueiGuideId}-${index}`} value={guide.satsueiGuideId}>
                      {guide.satsueiGuideName}
                    </option>
                  ))}
            </select>
              </div>
            )}
          </div>
        </div>
        </div>
      )}
      
      <Footer />

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />
    </div>
  );
};

export default AppSatsuei;